import React from 'react';
import { SectionData } from '../../hooks/usePdfExportStateV2';
import { EditableText } from './SectionRenderer';
import {
  SuiteEnvelope,
  RichText,
  TOOL_DISPLAY_NAMES,
  GRADE_STYLES,
  DELTA_STYLES,
  VOLATILITY_STYLES,
  PARSE_STATUS_STYLES,
  EVIDENCE_STATE_STYLES,
  BREADTH_CLASS_STYLES,
  quoteColumnLabel,
} from '../CyberSuiteReportRenderer';

/**
 * BETA suite envelopes (cyber, D&O, Home, CL, …) in the V2 export preview.
 *
 * Every suite tool returns the same envelope shape, so one set of section
 * types covers them all. Section `type` is `suite-<part>`; `contentKey` is
 * the envelope key ('' = the base report, otherwise the key under
 * `enhancements`), and `matrixIndex` selects a narrative section / data
 * table. Text goes through EditableText so the preview's edit mode works
 * exactly as it does for classic reports.
 */

const SUITE_TYPE_PREFIX = 'suite-';

export const isSuiteSectionType = (type: string): boolean => type.startsWith(SUITE_TYPE_PREFIX);

/** True when the export payload is a suite envelope rather than a classic report. */
export const isSuiteEnvelope = (data: any): boolean =>
  !!data &&
  !!data.responseMetadata &&
  Array.isArray(data.narrativeSections) &&
  !!data.inputsInventory &&
  !!data.reconciliation;

const toolNameFor = (env: SuiteEnvelope | undefined, fallbackKey = ''): string => {
  const type = env?.responseMetadata?.workflowType || fallbackKey.split(':')[0];
  return TOOL_DISPLAY_NAMES[type] || env?.responseMetadata?.toolVersion || type || 'Report';
};

/** Builds the flat section list for one envelope (base or enhancement). */
const sectionsForEnvelope = (env: SuiteEnvelope, envKey: string, startIndex: number): SectionData[] => {
  const sections: SectionData[] = [];
  let orderIndex = startIndex;
  const idBase = envKey ? `suite-enh-${envKey.replace(/[^a-z0-9]+/gi, '-')}` : 'suite';
  const titlePrefix = envKey ? `${toolNameFor(env, envKey)} — ` : '';
  const push = (part: string, title: string, matrixIndex?: number) => {
    sections.push({
      id: `${idBase}-${part}${matrixIndex !== undefined ? `-${matrixIndex}` : ''}`,
      title: `${titlePrefix}${title}`,
      type: `${SUITE_TYPE_PREFIX}${part}`,
      contentKey: envKey,
      matrixIndex,
      isVisible: true,
      orderIndex: orderIndex++,
    });
  };

  push('overview', envKey ? 'Overview' : 'Report Overview');
  push('executive-summary', 'Executive Summary');
  if ((env.ledger || []).length > 0) push('ledger', 'Ledger');
  if ((env.premiumBridge || []).length > 0) push('premium-bridge', 'Premium Bridge');
  if ((env.parseStatus || []).length > 0) push('parse-status', 'Parse Coverage');
  (env.dataTables || []).forEach((t, i) => push('data-table', t.title || `Data Table ${i + 1}`, i));
  (env.narrativeSections || []).forEach((s, i) => {
    const annex = s.layer === 'annex' ? 'Technical Annex — ' : '';
    push('narrative', `${annex}${s.title}`, i);
  });
  if ((env.researchScope || []).length > 0) push('research-scope', 'Research Scope — Verify Before Placement');
  push('reconciliation', 'Reconciliation');
  return sections;
};

/** Section list for a suite envelope plus each merged enhancement, in report order. */
export const parseSuiteEnvelopeToSections = (data: SuiteEnvelope): SectionData[] => {
  const sections = sectionsForEnvelope(data, '', 0);
  Object.entries(data.enhancements || {}).forEach(([key, env]) => {
    sections.push(...sectionsForEnvelope(env, key, sections.length));
  });
  return sections;
};

// --- rendering ---------------------------------------------------------------

/** Plain text for print: inline [CITE: …] markers and **bold** markers removed. */
const plain = (v?: RichText | string): string => {
  const raw = typeof v === 'string' ? v : v?.text || '';
  return raw
    .replace(/\[CITE:\s*"[^"]*"\]/g, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
};

// Light-mode only: the preview is a print surface.
const lightOnly = (cls?: string): string => (cls || 'bg-gray-100 text-gray-700').split(' ').filter((c) => !c.startsWith('dark:')).join(' ');

const Chip: React.FC<{ label: string; className?: string }> = ({ label, className }) => (
  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${lightOnly(className)}`}>{label}</span>
);

interface EditProps {
  isEditMode?: boolean;
  editedContent?: Map<string, string>;
  onContentChange?: (contentPath: string, value: string) => void;
}

interface SuiteSectionProps extends EditProps {
  section: SectionData;
  data: SuiteEnvelope;
}

export const SuiteSection: React.FC<SuiteSectionProps> = ({ section, data, isEditMode, editedContent, onContentChange }) => {
  const envKey = section.contentKey || '';
  const env: SuiteEnvelope | undefined = envKey ? data.enhancements?.[envKey] : data;
  if (!env) return null;

  const part = section.type.slice(SUITE_TYPE_PREFIX.length);
  const pathBase = `suite.${envKey || 'base'}`;
  const edit = (path: string, value: string, className = '', as: 'span' | 'p' | 'div' = 'span') => (
    <EditableText
      contentPath={`${pathBase}.${path}`}
      originalValue={value}
      editedContent={editedContent}
      onContentChange={onContentChange}
      isEditMode={isEditMode}
      className={className}
      as={as}
    />
  );

  switch (part) {
    case 'overview': {
      const meta = env.responseMetadata || ({} as SuiteEnvelope['responseMetadata']);
      const toolName = toolNameFor(env, envKey);
      return (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-1">{toolName}</h3>
            <div className="text-xs text-gray-600 flex flex-wrap gap-3">
              {meta.analysisDate && <span>Analysis date: {meta.analysisDate}</span>}
              {meta.frame && <span>Frame: {meta.frame}</span>}
              {meta.mode && <span>Mode: {meta.mode}</span>}
            </div>
            {meta.routedVia && (
              <p className="text-sm text-gray-700 mt-2">
                Automatic routing chose <strong>{toolName}</strong>
                {meta.routingRationale ? <> — {edit('routingRationale', meta.routingRationale)}</> : null}
              </p>
            )}
          </div>
          <div className="text-sm">
            <p className="font-semibold text-gray-700 mb-1">Documents received</p>
            <ul className="list-disc pl-5 space-y-1 text-gray-700">
              {(env.inputsInventory?.received || []).map((r, i) => (
                <li key={i}>{edit(`inputsInventory.received.${i}`, r)}</li>
              ))}
              {(env.inputsInventory?.received || []).length === 0 && <li className="italic text-gray-500">none</li>}
            </ul>
          </div>
          {(env.inputsInventory?.absent || []).length > 0 && (
            <div className="text-sm">
              <p className="font-semibold text-gray-700 mb-1">Not provided</p>
              <ul className="list-disc pl-5 space-y-1 text-gray-600">
                {(env.inputsInventory?.absent || []).map((a, i) => (
                  <li key={i}>{edit(`inputsInventory.absent.${i}`, a)}</li>
                ))}
              </ul>
            </div>
          )}
          {env.inputsInventory?.limitations?.text && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-gray-700">
              <h4 className="font-semibold text-amber-900 mb-1">Limitations</h4>
              {edit('inputsInventory.limitations', plain(env.inputsInventory.limitations), '', 'p')}
            </div>
          )}
        </div>
      );
    }

    case 'executive-summary':
      return (
        <div className="space-y-3 text-sm text-gray-700">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-1">Verdict</h4>
            {edit('executiveSummary.verdict', plain(env.executiveSummary?.verdict), 'text-base font-medium text-gray-900', 'p')}
          </div>
          {(env.executiveSummary?.paragraphs || []).map((p, i) => (
            <div key={i}>{edit(`executiveSummary.paragraphs.${i}`, plain(p), '', 'p')}</div>
          ))}
        </div>
      );

    case 'ledger': {
      const ledger = env.ledger || [];
      const isQuoteComparison = ledger.some((l) => (l.provisions?.length || 0) > 0);
      const hasPrior = !isQuoteComparison && ledger.some((l) => l.priorProvision);
      const quoteCols = Math.max(0, ...ledger.map((l) => l.provisions?.length || 0));
      const hasDelta = !isQuoteComparison && ledger.some((l) => l.deltaClass);
      return (
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-gray-100 text-left text-gray-700">
              <th className="p-1.5 border border-gray-200">Component</th>
              <th className="p-1.5 border border-gray-200">Standard</th>
              {hasPrior && <th className="p-1.5 border border-gray-200">Prior</th>}
              {quoteCols > 0
                ? Array.from({ length: quoteCols }, (_, i) => <th key={i} className="p-1.5 border border-gray-200">{quoteColumnLabel(ledger, i)}</th>)
                : <th className="p-1.5 border border-gray-200">Provision</th>}
              <th className="p-1.5 border border-gray-200">Grade</th>
              {hasDelta && <th className="p-1.5 border border-gray-200">Delta</th>}
              <th className="p-1.5 border border-gray-200">Materiality</th>
              <th className="p-1.5 border border-gray-200">Note</th>
            </tr>
          </thead>
          <tbody>
            {ledger.map((row, r) => (
              <tr key={row.key || r} className="align-top">
                <td className="p-1.5 border border-gray-200 font-medium text-gray-900">{row.key} · {row.component}</td>
                <td className="p-1.5 border border-gray-200 text-gray-600">{edit(`ledger.${r}.standardCriteria`, row.standardCriteria || '')}</td>
                {hasPrior && (
                  <td className="p-1.5 border border-gray-200 text-gray-700">{edit(`ledger.${r}.priorProvision`, plain(row.priorProvision) || '—')}</td>
                )}
                {quoteCols > 0
                  ? Array.from({ length: quoteCols }, (_, i) => (
                      <td key={i} className="p-1.5 border border-gray-200 text-gray-700">
                        {row.quoteCells?.[i] && (
                          <div className="flex flex-wrap gap-1 mb-1">
                            <Chip label={row.quoteCells[i].grade} className={GRADE_STYLES[row.quoteCells[i].grade]} />
                            <Chip label={row.quoteCells[i].evidenceState} className={EVIDENCE_STATE_STYLES[row.quoteCells[i].evidenceState]} />
                            <Chip label={row.quoteCells[i].breadthClass} className={BREADTH_CLASS_STYLES[row.quoteCells[i].breadthClass]} />
                          </div>
                        )}
                        {edit(`ledger.${r}.provisions.${i}`, plain(row.provisions?.[i]) || '—')}
                      </td>
                    ))
                  : (
                    <td className="p-1.5 border border-gray-200 text-gray-700">
                      {edit(`ledger.${r}.provision`, plain(row.provision) || '—')}
                      {row.classification && <div className="mt-1"><Chip label={row.classification} className="bg-indigo-100 text-indigo-800" /></div>}
                    </td>
                  )}
                <td className="p-1.5 border border-gray-200"><Chip label={row.grade} className={GRADE_STYLES[row.grade]} /></td>
                {hasDelta && (
                  <td className="p-1.5 border border-gray-200">{row.deltaClass ? <Chip label={row.deltaClass} className={DELTA_STYLES[row.deltaClass]} /> : null}</td>
                )}
                <td className="p-1.5 border border-gray-200"><Chip label={row.materiality} /></td>
                <td className="p-1.5 border border-gray-200 text-gray-600">{edit(`ledger.${r}.note`, row.note || '')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    case 'premium-bridge':
      return (
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-gray-100 text-left text-gray-700">
              {['Step', 'Component', 'Prior', 'Renewal', 'Δ', 'Driver'].map((h) => (
                <th key={h} className="p-1.5 border border-gray-200">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(env.premiumBridge || []).map((s, i) => (
              <tr key={i} className="align-top">
                <td className="p-1.5 border border-gray-200">{s.step}</td>
                <td className="p-1.5 border border-gray-200 font-medium">{s.component}</td>
                <td className="p-1.5 border border-gray-200">{edit(`premiumBridge.${i}.priorValue`, s.priorValue || '—')}</td>
                <td className="p-1.5 border border-gray-200">{edit(`premiumBridge.${i}.renewalValue`, s.renewalValue || '—')}</td>
                <td className="p-1.5 border border-gray-200 font-medium">{edit(`premiumBridge.${i}.delta`, s.delta || '—')}</td>
                <td className="p-1.5 border border-gray-200">{edit(`premiumBridge.${i}.driver`, `${s.driver}${s.note ? ` — ${s.note}` : ''}`)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );

    case 'parse-status':
      return (
        <ul className="space-y-1 text-sm">
          {(env.parseStatus || []).map((it, i) => (
            <li key={i} className="flex items-start gap-2">
              <Chip label={it.status} className={PARSE_STATUS_STYLES[it.status]} />
              <span className="text-gray-700">{it.docName}{it.note ? ` — ${it.note}` : ''}</span>
            </li>
          ))}
        </ul>
      );

    case 'data-table': {
      const table = env.dataTables?.[section.matrixIndex ?? 0];
      if (!table) return null;
      return (
        <div>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-gray-100 text-left text-gray-700">
                {table.columns.map((c, i) => <th key={i} className="p-1.5 border border-gray-200">{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {(table.rows || []).map((row, r) => (
                <tr key={r} className="align-top">
                  {row.map((cell, c) => (
                    <td key={c} className="p-1.5 border border-gray-200 text-gray-700">
                      {edit(`dataTables.${section.matrixIndex}.rows.${r}.${c}`, cell ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {table.notes && <p className="text-xs text-gray-500 italic mt-1">{edit(`dataTables.${section.matrixIndex}.notes`, table.notes)}</p>}
        </div>
      );
    }

    case 'narrative': {
      const narrative = env.narrativeSections?.[section.matrixIndex ?? 0];
      if (!narrative) return null;
      return (
        <div className="space-y-2 text-sm text-gray-700">
          {(narrative.blocks || []).map((b, i) => (
            <div key={i}>{edit(`narrativeSections.${section.matrixIndex}.blocks.${i}`, plain(b), '', 'p')}</div>
          ))}
        </div>
      );
    }

    case 'research-scope':
      return (
        <div className="space-y-3">
          {(env.researchScope || []).map((item, i) => (
            <div key={i} className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
              <div className="flex items-center gap-2 mb-1">
                <Chip label={item.volatility} className={VOLATILITY_STYLES[item.volatility]} />
                <h4 className="font-medium text-gray-900">{edit(`researchScope.${i}.item`, item.item)}</h4>
              </div>
              {edit(`researchScope.${i}.datedNote`, item.datedNote, 'text-gray-700', 'p')}
              {item.bothSideWording && edit(`researchScope.${i}.bothSideWording`, item.bothSideWording, 'text-xs text-gray-500 italic mt-1', 'p')}
            </div>
          ))}
        </div>
      );

    case 'reconciliation':
      return (
        <div className="space-y-2 text-sm text-gray-700">
          <Chip
            label={env.reconciliation?.countsTieOut ? 'Tie-out confirmed' : 'Tie-out NOT confirmed'}
            className={env.reconciliation?.countsTieOut ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
          />
          {edit('reconciliation.statement', plain(env.reconciliation?.statement), '', 'p')}
        </div>
      );

    default:
      return null;
  }
};
